import { select, put, call, fork } from 'redux-saga/effects';
import { List, fromJS } from 'immutable';
import log from 'loglevel';

import {
    SELECT_QUIZ_TASK_ITEM,
    OPEN_QUIZ_STAGE,
    SUBMIT_QUIZ_ANSWERS,
    setQuizTaskStage,
    clearQuizState,
    EXIT_QUIZ,
    setPreviousQuizResults,
    setQuizRewards,
} from './actions';
import { throttledTakeEvery } from 'phoenix2/utils';
import { startAlert } from 'phoenix2/state/alert/actions';
import { taskViewOpened } from 'phoenix2/state/viewstates/actions';
import { wCall } from 'phoenix2/state/wait/sagas';
import { request, notify } from 'phoenix2/state/remote/sagas';
import { getTaskItemState, getTaskState } from 'phoenix2/state/progress/selectors';
import { getTask } from 'phoenix2/state/content/selectors_ts';
import {
    getQuestionActivities,
    getQuestionActivity,
    getQuestionActivityId,
    getShowingQuestionAttemptId,
} from 'phoenix2/state/question-activity/selectors';
import {
    removeQuestionActivity,
    setAnswersSubmitted,
} from 'phoenix2/state/question-activity/actions';
import { getCurrentTaskId } from 'phoenix2/state/viewstates/selectors';
import { showAlert } from 'phoenix2/state/alert/sagas';
import {
    onRequestPopTaskView,
    openStandardTaskView,
    openView,
    setupSkillDeliveryView,
} from 'phoenix2/state/views/sagas';
import { buildQuestionAnswers } from 'phoenix2/utils/question';
import { parseTaskID } from 'phoenix2/utils/task';
import { startExplicitWaiting, stopExplicitWaiting } from 'phoenix2/state/wait/actions';
import { getQuizRewards, getQuizTaskStage } from './selectors';
import { startTaskItemActivity } from 'phoenix2/state/viewstates/sagas';
import { ensureSynced } from 'phoenix2/state/sync/sagas';
import { popView } from 'phoenix2/state/views/actions';
import { analyticsHit } from 'phoenix2/state/analytics/actions';
import {
    updatePackageStates,
    updateTaskItemStates,
    updatePackageTaskStates,
} from 'phoenix2/state/progress/actions';
import { TASK_VIEW_ID } from '../viewstates/view-ids';
import { QuizStage } from 'phoenix2/state/quiz/constants.ts';
import { isFTQQuizItem, isFTQTask } from 'phoenix2/utils/ftqtask';
import { showMinigameSelection, showRewards } from '../question-activity/sagas';
import { getFeatureFlag } from 'phoenix2/state/feature-flags/selectors';
import { FEATURE_FLAG_FTQ_UX_IMPROVEMENTS } from 'phoenix2/state/feature-flags';
import { updateTasks } from 'phoenix2/state/content/actions';
import { abortFTQTask } from 'phoenix2/state/ftq-task/actions';

/**
 * Returns whether a quiz has previously been completed.
 */
function* isQuizAlreadyCompleted(taskId) {
    // Load previous quiz results from the server.
    const response = yield wCall(request, 'getQuizResults');
    const results = response.get('results', List());
    yield put(setPreviousQuizResults(results));

    // See if this task is in the previous results.
    const { packageID, taskIndex } = parseTaskID(taskId);
    return isTaskInResults(results, packageID, taskIndex);
}

/**
 * Finds the first incomplete task item in a quiz and selects it.
 */
function* selectFirstIncompleteQuestion(task) {
    const items = yield call(getIncompleteItems, task);
    if (items.size === 0) {
        return;
    }
    yield call(doSelectQuizTaskItem, { item: items.get(0) });
}

function* cleanupQuiz(taskId) {
    // Dismiss the current activity if the quiz hasn't been submitted.
    const currentAttemptId = yield select(getShowingQuestionAttemptId);
    if (currentAttemptId) {
        const activity = yield select(getQuestionActivity, currentAttemptId);
        const quizTaskStage = yield select(state => getQuizTaskStage(state, taskId));
        // The quiz will be at the "Results" stage if it has been submitted.
        if (activity && quizTaskStage !== QuizStage.Results) {
            yield call(notify, 'exitTaskItem', activity.getIn(['info', 'question_activity_id']));
        }
    }

    // Clear up all of the question activities.
    const activities = yield select(getQuestionActivities);
    for (const act of activities.entries()) {
        yield put(removeQuestionActivity(act[0]));
    }

    // Clear up the state of the quiz.
    yield put(clearQuizState(taskId));
}

/**
 * Called to start a quiz. We get previous results to see if they have already taken this task as a
 * quiz, and if they haven't open the quiz view.
 * @param task - the task
 * @param viewId - the view Id e.g. 'task'
 * @param method - a method for changing the view. openStandardTaskView will default this to 'push'.
 * @param state - the taskState that the quiz should be opened in, should be STATE_QUIZ_ANSWER
 * or STATE_QUIZ_VIEW (also the empty string value).
 */
export function* openQuizTaskView(task, method, state = '') {
    const taskId = task.get('task_id');
    const ftqUXImprovementsFlag = yield select(state =>
        getFeatureFlag(state, FEATURE_FLAG_FTQ_UX_IMPROVEMENTS),
    );

    let result;
    if (task.get('task_type') !== 'INDY_QUIZ') {
        // If they have completed the quiz, fall back to the normal view.
        const alreadyCompleted = yield call(isQuizAlreadyCompleted, taskId);
        if (alreadyCompleted) {
            yield call(openStandardTaskView, taskId, method);
            return;
        }
        if (!ftqUXImprovementsFlag) {
            // Inform the student that they are about to take a quiz and give them the option to back out.
            result = yield call(showAlert, {
                title: 'You are about to begin your quiz',
                message:
                    'Make sure you write down your answers alongside question numbers. ' +
                    'After the quiz you will need to input your answers using ' +
                    'your workings.',
                buttons: [
                    { key: 'back', text: 'Back', secondary: true },
                    { key: 'start', text: 'Start', primary: true },
                ],
            });
            if (result === 'back') {
                return;
            }
        } else {
            result = 'start';
        }
    } else {
        result = 'start';
    }

    // Wait for the state for this task to be loaded.
    yield call(ensureSynced, 'taskItemStates', taskId);

    // Select first task item.
    yield call(selectFirstIncompleteQuestion, task);

    // This is only done if the start button is clicked - there is a chance that you could
    // leave the modal without clicking start if a teacher has moved the student onto the
    // answer stage without the student clicking an option on the first modal.
    if (result === 'start') {
        // Set the correct quiz stage
        const stage = state === 'STATE_QUIZ_ANSWER' ? QuizStage.Answering : QuizStage.Viewing;
        yield put(setQuizTaskStage(taskId, stage));
    }

    try {
        yield call(
            openView,
            'push',
            {
                type: 'quiz',
                viewId: TASK_VIEW_ID,
            },
            {
                onViewOpenedAction: taskViewOpened(),
                onRequestPop: function*() {
                    if (ftqUXImprovementsFlag) {
                        const response = yield call(showAlert, {
                            title: 'Exit Fast Track Quiz?',
                            type: 'leaveFastTrackQuiz',
                            buttons: [
                                { key: 'cancel', text: 'Continue quiz', secondary: true },
                                { key: 'confirm', text: 'Exit quiz' },
                            ],
                        });
                        if (response === 'confirm') {
                            yield abortFTQ(taskId);
                        }
                    } else {
                        yield call(onRequestPopTaskView, taskId);
                    }
                },
            },
        );
    } finally {
        yield call(cleanupQuiz, taskId);
    }
}

/**
 * Aborts the FTQ. It will first pause the quiz activity if needed then
 * call the server, send some analytics then finally return the user to the
 * task view
 * @param taskId
 * @returns {Generator<*, void, *>}
 */
function* abortFTQ(taskId) {
    const { packageID, taskIndex } = parseTaskID(taskId);
    // Need to pause the current activity, so it's properly aborted
    const currentAttemptId = yield select(getShowingQuestionAttemptId);
    if (currentAttemptId) {
        const questionActivityId = yield select(getQuestionActivityId, currentAttemptId);
        yield call(pauseQuizActivity, questionActivityId);
    }
    yield put(abortFTQTask(taskId));
    yield put(
        analyticsHit('event', {
            eventCategory: 'quiz',
            eventAction: 'Abort quiz',
            eventLabel: { packageID, taskIndex },
        }),
    );
    yield call(onRequestPopTaskView, taskId);
}

export function* watchSelectQuizTaskItem() {
    yield throttledTakeEvery(SELECT_QUIZ_TASK_ITEM, doSelectQuizTaskItem, 0);
}

/**
 * Selects a quiz type task item. This is used in a number of scenarios:
 * - A user selects a quiz which, unlike a normal task, automatically selects the first task item.
 * - A user has finished working on a quiz type task and has chosen to finish the quiz. Marking mode
 *   will be turned on and they will be taken to the first task item.
 * - A user has finished a quiz but got at least one answer wrong. They have the option to retry and
 *   will be taken to the first remaining incorrect task item.
 * - A user is already on a task item in a quiz and is navigating to another task item. In this case
 *   we handle checking and submitting the answer to the current attempt before moving to the new
 *   task item.
 * In all cases we make a server call to get a new activity and then to start the new activity.
 *
 * @param action.item - item is the task item to select
 * @param action.notCurrentlyInTaskItem - if notCurrentlyInTaskItem is true, there is no task item already in
 * progress so we do not attempt to handle any existing answers.
 */
export function* doSelectQuizTaskItem(action) {
    const { item, notCurrentlyInTaskItem } = action;

    const currentAttemptId = yield select(getShowingQuestionAttemptId);
    const mode = yield select(getQuizTaskStage, item.get('task_id'));

    // Check if we're moving away from a current task item. If we're in marking mode then we need to
    // submit our answer. If we're not then we just need to pause the activity.
    if (currentAttemptId && !notCurrentlyInTaskItem) {
        if (mode === QuizStage.Answering) {
            const choice = yield call(trySubmitQuizAnswer, currentAttemptId);
            if (choice === 'back') {
                return;
            }
        } else {
            const questionActivityId = yield select(getQuestionActivityId, currentAttemptId);
            yield call(pauseQuizActivity, questionActivityId);
        }
    }

    const info = yield call(startTaskItemActivity, item, 'RESUMABLE', 'QUIZ');
    if (!info) {
        return;
    }

    const itemId = item.get('id');
    const [packageID, taskIndex, taskItemIndex] = itemId.split('/');
    yield put(
        analyticsHit('event', {
            eventCategory: 'quiz',
            eventAction: 'select item',
            eventLabel: { packageID, taskIndex, taskItemIndex },
        }),
    );
    yield call(setupSkillDeliveryView, info, item.get('id'), false, undefined, true, 'QUIZ');
}

export function* watchOpenQuizStage() {
    yield throttledTakeEvery(OPEN_QUIZ_STAGE, doOpenQuizStage, 0);
}

// Map of quiz stages to handlers for when doOpenQuizStage is called.
const quizStageHandlers = {
    [QuizStage.Viewing]: doOpenQuizViewStage,
    [QuizStage.Answering]: doOpenQuizAnswerStage,
};

/**
 * Called when the user should transition to a new quiz stage. If the user is already in the
 * requested stage then no action would be taken.
 * For answering mode it is called when the user has finished working on the quiz and wants to
 * start entering answers, i.e. go into marking mode. If they have not been forced, we check
 * they have looked at all the questions before allowing them to continue.
 * @param action.force - if they have been forced to come into marking mode e.g. the quiz timer has
 * expired
 */
function* doOpenQuizStage(action) {
    const { stage, options } = action;

    // Check if the student is already in the requested stage. If so then we ignore
    // the request to change.
    const currentTaskId = yield select(getCurrentTaskId);
    const taskId = options.taskId || currentTaskId;
    if (currentTaskId !== taskId) {
        return null; // No action as it's a different task, ignore it
    }

    const currentStage = yield select(getQuizTaskStage, taskId);
    if (currentStage === stage) {
        return null;
    }

    // If set in the options, check if the quiz is already completed and don't apply the
    // action if so. This is used if a lesson position update is dispatched as we don't
    // want to change the task state if they have already completed the quiz.
    if (options.checkIncomplete) {
        const alreadyCompleted = yield call(isQuizAlreadyCompleted, taskId);
        if (alreadyCompleted) {
            return null;
        }
    }

    // Handle the change to the requested stage
    const handler = quizStageHandlers[stage];
    if (handler) {
        yield call(handler, action, taskId);
    }
}

function* doOpenQuizViewStage(action, taskId) {
    // Change task state to viewing mode
    yield put(setQuizTaskStage(taskId, QuizStage.Viewing));
}

function* doOpenQuizAnswerStage(action, taskId) {
    const { options } = action;
    const { force } = options || {};
    const task = yield select(getTask, taskId);
    const items = yield getIncompleteItems(task);

    let choice = 'forced';
    if (!force) {
        choice = yield call(showFinishedAnsweringQuizAlert);
    }

    // Track the move to answer entry
    const { packageID, taskIndex } = parseTaskID(taskId);
    yield put(
        analyticsHit('event', {
            eventCategory: 'quiz',
            eventAction: 'choose answer entry',
            eventLabel: { packageID, taskIndex, choice },
        }),
    );

    // They're not continuing so we back out
    if (choice === 'back') {
        return false;
    }

    const ftqUXImprovementsFlag = yield select(state =>
        getFeatureFlag(state, FEATURE_FLAG_FTQ_UX_IMPROVEMENTS),
    );
    if (!ftqUXImprovementsFlag) {
        yield put(
            startAlert({
                title: 'Enter your answers',
                message:
                    'Please enter the answers you gave during the quiz. Make sure you are ' +
                    'entering answers for the correct question number.',
                buttons: [{ key: 'continue', text: 'Start', primary: true }],
            }),
        );
    }

    // Open the first task item
    if (items && items.count() > 0) {
        const item = items.get(0);
        yield call(doSelectQuizTaskItem, { item, notCurrentlyInTaskItem: false });
    }

    // Change task state to marking mode
    yield put(setQuizTaskStage(taskId, QuizStage.Answering));
}

export function* watchSubmitQuizAnswers() {
    yield throttledTakeEvery(SUBMIT_QUIZ_ANSWERS, doSubmitQuizAnswers, 0);
}

/**
 * Tells the server to find all the activities which relate to the user's answers to the quiz task,
 * submit them, and work out the quiz result. In situations where the user is already on a task item
 * we handle checking and submitting the answer to the current attempt before submitting.
 * Once we have checked the current attempt, we submit the answers and update the task items states.
 * @param action.taskId - the task id of the quiz to submit
 */
export function* doSubmitQuizAnswers(action) {
    const { taskId, noPrompt } = action;

    // Check the answer state of the current answer so the Invalid answer input
    // modal can appear if needed. We don't submit the answer yet, as the user
    // will still be able to back out after this
    const currentAttemptId = yield select(getShowingQuestionAttemptId);
    let activity;
    if (currentAttemptId) {
        activity = yield select(getQuestionActivity, currentAttemptId);
        const choice = yield call(checkActivityAnswerState, activity);
        if (choice === 'back') {
            return false;
        }
    }

    // Confirm if the user would like to finish the quiz
    if (!noPrompt) {
        const choice = yield call(showFinishedEnteringQuizAnswersAlert);
        if (choice === 'back') {
            return false;
        }
    }

    // Submit the current answer, any confirm modals should have already appeared
    // and been accepted, so we just submit.
    if (currentAttemptId) {
        const questionActivityId = yield select(getQuestionActivityId, currentAttemptId);
        yield call(submitQuizAnswer, activity, currentAttemptId, questionActivityId);
    }

    // Track the answer submission
    const { packageID, taskIndex } = parseTaskID(taskId);
    yield put(
        analyticsHit('event', {
            eventCategory: 'quiz',
            eventAction: 'submit answers',
            eventLabel: { packageID, taskIndex },
        }),
    );

    // Submit the quiz, update the state of the questions with the result
    // TODO: this doesn't show a wait anim in Numerise
    yield put(startExplicitWaiting('Submitting answers...', true));

    const response = yield call(request, 'submitQuiz', taskId);
    const task = yield select(getTask, taskId);

    let quizComplete = true;
    if (task.get('task_type') !== 'INDY_QUIZ') {
        // Load the result of the quiz - we do this before we update the package states
        // so that there is less chance of the screen updating with the results before
        // the transition to the result screen
        quizComplete = yield isQuizAlreadyCompleted(taskId);

        // set the quiz rewards, we don't do this for indy as it handles XP differently
        yield put(setQuizRewards(taskId, response.get('rewards')));
    }

    yield put(updateTaskItemStates(response.get('itemStates')));
    yield put(updatePackageTaskStates(response.get('taskStates')));
    yield put(updatePackageStates(response.get('pkgStates')));
    yield put(updateTasks(response.get('tasksUpdate')));

    yield put(stopExplicitWaiting());

    // If it is an Indy quiz then skip the results page
    if (task.get('task_type') === 'INDY_QUIZ') {
        yield put(popView());
        return;
    }

    // Check if the quiz is actually finished
    if (quizComplete) {
        yield put(setQuizTaskStage(taskId, QuizStage.Results));
        return;
    }

    yield call(promptQuizRetry, action);
}

export function* promptQuizRetry(action) {
    const { taskId } = action;
    const task = yield select(getTask, taskId);

    // Clear up all of the question activities.
    const activities = yield select(getQuestionActivities);
    for (const act of activities.entries()) {
        yield put(removeQuestionActivity(act[0]));
    }

    const taskState = yield select(getTaskState, taskId);

    const correct = taskState.getIn(['progress', 'S'], 0);
    let total = taskState.get('size', 0);
    if (isFTQTask(task)) {
        // Not all items in fast track quiz tasks are in the quiz
        total = task.getIn(['labels', 'ftq_quiz_count'], total);
    }

    const choice = yield call(showAlert, {
        title: `Quiz score: ${correct}/${total}`,
        message:
            'Check that you entered your answers correctly and retry questions to improve your score.',
        buttons: [
            { key: 'finish', text: 'End quiz', secondary: true },
            { key: 'retry', text: 'Improve score', primary: true },
        ],
    });

    if (choice === 'finish') {
        // Resubmit the answers to notify the server that we've finished
        yield call(doSubmitQuizAnswers, { ...action, noPrompt: true });
        return;
    }

    // Open the first incomplete question
    yield put(setQuizTaskStage(taskId, QuizStage.Viewing));
    yield call(selectFirstIncompleteQuestion, task);
}

export function* watchExitQuiz() {
    yield throttledTakeEvery(EXIT_QUIZ, doExitQuiz, 0);
}

/**
 * Called when the user has viewed the results and chooses to exit the quit. It they are in a fast
 * track quiz task, the view is just popped. If they are in any other quiz type task, they are shown
 * an alert and taken to the task view.
 * @param action.taskId - the task id of the quiz to quit
 */
export function* doExitQuiz(action) {
    const { taskId } = action;
    // Load the quiz rewards, we do this now as they will be cleaned up when we pop the view
    const rewards = yield select(getQuizRewards, taskId);

    // For fast track quiz we don't do quiz review.
    const task = yield select(getTask, taskId);
    if (isFTQTask(task)) {
        yield put(popView());

        // Show the rewards and minigame if we have any
        yield call(showRewards, rewards);
        yield call(showMinigameSelection, rewards);
        return;
    }

    // If they have completed the quiz to 100%, don't go into quiz review
    const taskState = yield select(getTaskState, taskId);
    if (taskState.get('complete')) {
        yield put(popView());

        // Show the rewards and minigame if we have any
        yield call(showRewards, rewards);
        yield call(showMinigameSelection, rewards, false);
        return;
    }

    // Transition to the quiz summary on the task view
    yield fork(showAlert, {
        title: 'Review Quiz',
        message:
            'To complete this task you need to correctly answer each ' +
            'question you got incorrect in the quiz. You can use the videos ' +
            'to help you.',
        buttons: [{ key: 'continue', text: 'Start', primary: true }],
    });
    yield call(
        openView,
        'replace',
        {
            type: 'task',
            viewId: TASK_VIEW_ID,
        },
        {
            onViewOpenedAction: taskViewOpened(),
            onRequestPop: () => onRequestPopTaskView(taskId),
        },
    );
}

// Attempts to submit the quiz answer, will check with the user if answer input is invalid
function* trySubmitQuizAnswer(attemptId) {
    const activity = yield select(getQuestionActivity, attemptId);
    const questionActivityId = yield select(getQuestionActivityId, attemptId);

    const choice = yield call(checkActivityAnswerState, activity);
    if (choice === 'back') {
        return choice;
    }

    yield call(submitQuizAnswer, activity, attemptId, questionActivityId);
}

// checks the answer state, displaying an alert if needed
function* checkActivityAnswerState(activity) {
    switch (getActivityAnswerState(activity)) {
        case ANSWER_INVALID:
            const choice = yield call(showInvalidAnswerAlert);
            if (choice === 'back') {
                return choice;
            }
    }
}

// submits the quiz answer depending on it's state, assumes any checking with
// the user has already been done
function* submitQuizAnswer(activity, attemptId, questionActivityId) {
    switch (getActivityAnswerState(activity)) {
        case ANSWER_INVALID:
            yield call(clearAnswerAndSetNotSubmitted, activity, questionActivityId);
            break;
        case ANSWER_VALID:
            yield call(storeAnswerAndSetSubmitted, activity, attemptId);
            break;
        case ANSWER_UNATTEMPTED:
            yield call(pauseQuizActivity, questionActivityId);
            break;
    }
}

/**
 * Returns an immutable list of incomplete task items
 * @param task - the task
 */
export function* getIncompleteItems(task) {
    const items = task.get('items');
    const filteredItems = [];
    const ftqTask = isFTQTask(task);
    for (const item of items.values()) {
        const taskItemState = yield select(getTaskItemState, item.get('id'));
        if (
            !taskItemState.get('done', false) && // Only incomplete task items
            taskItemState.get('status') !== 'HIDDEN' && // Only those not hidden
            (!ftqTask || isFTQQuizItem(item)) // In fast track quiz tasks, only the quiz items
        ) {
            filteredItems.push(item);
        }
    }
    return fromJS(filteredItems);
}

const ANSWER_INVALID = 'invalid';
const ANSWER_UNATTEMPTED = 'unattempted';
const ANSWER_VALID = 'valid';

/**
 * Returns the state of the user interacting with an answer.
 * @param activity - the activity
 */
function getActivityAnswerState(activity) {
    if (!activity) {
        return;
    }
    switch (activity.getIn(['info', 'pc', 'valid'])) {
        case false:
            return ANSWER_INVALID;
        case true:
            return ANSWER_VALID;
        case undefined:
            return ANSWER_UNATTEMPTED;
    }
}

function* showInvalidAnswerAlert() {
    return yield call(showAlert, {
        message: 'You have not entered a fully valid answer. Would you like to continue?',
        buttons: [
            { key: 'back', text: 'Back', secondary: true },
            { key: 'continue', text: 'Continue', primary: true },
        ],
    });
}

/**
 * Shows the user an alert asking if they have finished answering a quiz
 */
function* showFinishedAnsweringQuizAlert() {
    let title = 'Have you finished answering?';
    let message =
        'Write down your answers to all of the questions before entering your answers. Once you ' +
        "start entering your answers you won't be able to see the questions again.";
    let buttonText = "I've finished answering";

    const ftqUXImprovementsFlag = yield select(state =>
        getFeatureFlag(state, FEATURE_FLAG_FTQ_UX_IMPROVEMENTS),
    );
    if (ftqUXImprovementsFlag) {
        title = 'Have you finished your working?';
        message =
            'Make sure you have written down all of your questions, workings and answers. Once ' +
            "you start entering your answers, you won't be able to see the questions again.";
        buttonText = "I've finished my working";
    }

    return yield call(showAlert, {
        title,
        buttons: [
            { key: 'back', text: 'Back to questions', secondary: true },
            { key: 'continue', text: buttonText, primary: true },
        ],
        className: 'alert-box--wide', // use a wider variant
        message,
    });
}

/**
 * Shows the user an alert asking if they have finished entering their answers for a quiz
 */
function* showFinishedEnteringQuizAnswersAlert() {
    const ftqUXImprovementsFlag = yield select(state =>
        getFeatureFlag(state, FEATURE_FLAG_FTQ_UX_IMPROVEMENTS),
    );
    let alertContents = {
        title: 'Finished entering answers?',
        buttons: [
            { key: 'back', text: 'Change answers', secondary: true },
            { key: 'continue', text: "I've finished", primary: true },
        ],
    };
    if (ftqUXImprovementsFlag) {
        alertContents = {
            title: 'Finish entering answers',
            buttons: [
                { key: 'back', text: 'Change answers', secondary: true },
                { key: 'continue', text: 'Submit answers', primary: true },
            ],
            message:
                'This will submit your answers and mark your quiz, are you sure ' +
                'you have entered all of your answers?',
        };
    }
    return yield call(showAlert, alertContents);
}

function* clearAnswerAndSetNotSubmitted(activity, currentAttemptId) {
    yield wCall(request, 'clearQuizAnswer', activity.getIn(['info', 'question_activity_id']));
    yield put(setAnswersSubmitted(currentAttemptId, false));
}

function* storeAnswerAndSetSubmitted(activity, currentAttemptId) {
    const answer = buildQuestionAnswers(activity.getIn(['info', 'pc']));
    yield wCall(
        request,
        'storeQuizAnswer',
        activity.getIn(['info', 'question_activity_id']),
        answer,
    );
    yield put(setAnswersSubmitted(currentAttemptId, true));
}

function* pauseQuizActivity(id) {
    // We use a request, instead of a notify, to send the activity action, as we
    // want to wait for a response before we allow the user to start storing an answer
    // for the activity.
    if (id) {
        try {
            yield wCall(request, 'pauseQuizActivity', id);
        } catch (e) {
            log.info('Failed to pause task, returning:', e);
        }
    }
}

function isTaskInResults(results, packageID, taskIndex) {
    return results.find(
        res =>
            res.get('task').get('packageID') === packageID &&
            res.get('task').get('taskIndex') === taskIndex,
    );
}
